'use client';

import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'react-feather';
import { Image } from '@wilkins-software/redwood-utils';

export type ArticleImageLightboxItem = {
  src: string;
  alt: string;
  caption?: string;
};

type ArticleImageLightboxProps = {
  open: boolean;
  onClose: () => void;
  items: ArticleImageLightboxItem[];
  activeIndex: number;
  onActiveIndexChange?: (nextIndex: number) => void;
};

/**
 * Full-viewport image overlay for article figures and galleries.
 * Original assets are shown untransformed so letterboxed article thumbs are not cropped.
 */
export function ArticleImageLightbox({
  open,
  onClose,
  items,
  activeIndex,
  onActiveIndexChange,
}: ArticleImageLightboxProps) {
  const titleId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [canUseDomPortal, setCanUseDomPortal] = useState(false);

  const itemCount = items.length;
  const safeActiveIndex =
    itemCount === 0 ? 0 : Math.min(Math.max(activeIndex, 0), itemCount - 1);
  const activeItem = items[safeActiveIndex];
  const hasMultiple = itemCount > 1;

  useEffect(() => {
    setCanUseDomPortal(true);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handleWindowKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    }

    window.addEventListener('keydown', handleWindowKeyDown);
    return () => {
      window.removeEventListener('keydown', handleWindowKeyDown);
    };
  }, [open, onClose]);

  if (!canUseDomPortal || !open || !activeItem) {
    return null;
  }

  function goToPrevious() {
    if (!hasMultiple || !onActiveIndexChange) {
      return;
    }
    const nextIndex =
      safeActiveIndex === 0 ? itemCount - 1 : safeActiveIndex - 1;
    onActiveIndexChange(nextIndex);
  }

  function goToNext() {
    if (!hasMultiple || !onActiveIndexChange) {
      return;
    }
    const nextIndex =
      safeActiveIndex === itemCount - 1 ? 0 : safeActiveIndex + 1;
    onActiveIndexChange(nextIndex);
  }

  function handleDialogKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      event.stopPropagation();
      goToPrevious();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      goToNext();
    }
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={handleDialogKeyDown}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/90 p-3 sm:p-6"
      onClick={onClose}
    >
      <p id={titleId} className="sr-only">
        {activeItem.alt}
      </p>

      <button
        ref={closeButtonRef}
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
        aria-label="Close full-size image"
        className="absolute top-3 right-3 z-[2] flex size-10 items-center justify-center rounded-full border border-white/20 bg-slate-900/80 text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-slate-800 sm:top-5 sm:right-5"
      >
        <X className="size-5" aria-hidden />
      </button>

      {hasMultiple ? (
        <>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              goToPrevious();
            }}
            aria-label="Previous image"
            className="absolute top-1/2 left-2 z-[2] flex size-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-slate-900/80 text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-slate-800 sm:left-4"
          >
            <ChevronLeft className="size-5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              goToNext();
            }}
            aria-label="Next image"
            className="absolute top-1/2 right-2 z-[2] flex size-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-slate-900/80 text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-slate-800 sm:right-4"
          >
            <ChevronRight className="size-5" aria-hidden />
          </button>
        </>
      ) : null}

      <div
        className="flex max-h-full w-full max-w-6xl flex-col items-center gap-3"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex min-h-0 w-full flex-1 items-center justify-center">
          <Image
            src={activeItem.src}
            alt={activeItem.alt}
            unoptimized
            className="max-h-[min(85vh,56rem)] w-auto max-w-full object-contain"
          />
        </div>

        {(activeItem.caption || hasMultiple) && (
          <div className="max-w-3xl px-2 text-center text-sm leading-5 text-slate-200">
            {activeItem.caption ? <p className="m-0">{activeItem.caption}</p> : null}
            {hasMultiple ? (
              <p className="mt-1 mb-0 text-xs font-medium tracking-wide text-slate-400">
                {safeActiveIndex + 1} / {itemCount}
              </p>
            ) : null}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

type ArticleFigureLightboxMediaProps = {
  src: string;
  alt: string;
  caption?: string;
  sizes: string;
  imageClassName: string;
};

/**
 * Clickable article figure media that opens {@link ArticleImageLightbox}.
 */
export function ArticleFigureLightboxMedia({
  src,
  alt,
  caption,
  sizes,
  imageClassName,
}: ArticleFigureLightboxMediaProps) {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsLightboxOpen(true)}
        aria-label={`View full-size image: ${alt}`}
        className="group relative block w-full cursor-zoom-in rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
      >
        <Image src={src} alt={alt} sizes={sizes} className={imageClassName} />
        <span className="pointer-events-none absolute inset-0 rounded-lg ring-0 transition-[box-shadow] group-hover:ring-1 group-hover:ring-inset group-hover:ring-slate-900/10" />
      </button>

      <ArticleImageLightbox
        open={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        items={[{ src, alt, caption }]}
        activeIndex={0}
      />
    </>
  );
}
